import { FC, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Button } from '@factorialco/f0-react'
import { Add } from '@factorialco/f0-react/icons/app'
import { Reorder } from 'framer-motion'
import { v4 as uuid } from 'uuid'

import i18n from 'modules/core/lib/i18n'

import { Element } from 'modules/forms/lib/Element'
import { ElementOption } from 'modules/forms/lib/types'

import { ChoiceElement } from './ChoiceElement'

interface ChoiceElementEditorProps {
  element: Element
  enableCorrectConfiguration: boolean
  onChange: (element: Element) => void
}

// we work locally with an 'uuid' field to work with new options without 'id'
type UniqueOption = ElementOption & { uuid: string }

/**
 * Editor component for SingleChoiceElement & MultipleChoiceElement
 */
const ChoiceElementEditor: FC<ChoiceElementEditorProps> = ({
  element,
  enableCorrectConfiguration,
  onChange,
}) => {
  //we create a copy of the element options to work here with an uuid for each one
  const [options, setOptions] = useState<UniqueOption[]>(() => {
    return element.options.map((option) => ({
      ...option,
      uuid: uuid(),
    }))
  })

  /**
   * Stores the options locally and also in the element (parent)
   *
   * @param newOpts
   */
  const updateOptions = (newOpts: UniqueOption[]) => {
    setOptions(newOpts)
    element.options = newOpts
    onChange(element)
  }

  const onReorderOptions = (newOpts: UniqueOption[]) => {
    updateOptions([...newOpts])
  }

  const handleElementOptionChange = (index: number, label: string) => {
    if (index >= 0 && index < options.length && options[index]) {
      options[index].label = label
      updateOptions([...options])
    }
  }

  const handleElementOptionCorrectChange = (index: number, isCorrect: boolean) => {
    if (index >= 0 && index < options.length && options[index]) {
      options[index].isCorrect = isCorrect
      updateOptions([...options])
    }
  }

  const handleRemoveOption = (index: number) => {
    options.splice(index, 1)
    updateOptions([...options])
  }

  const addChoiceElement = async () => {
    const newOptions = [
      ...options,
      {
        label: i18n.t('forms.editor.options.option_placeholder', { position: options.length + 1 }),
        value: '',
        id: '',
        isCorrect: false,
        uuid: uuid(),
      },
    ]
    updateOptions([...newOptions])
  }

  const errors = element.validate(enableCorrectConfiguration)

  return (
    <>
      <Text size="200" className="text-f1-foreground-secondary" weight="medium">
        {i18n.t('forms.editor.options.title')}
      </Text>
      {errors.options && <F0Alert variant="warning" title={errors.options} description="" />}
      <Reorder.Group axis="y" values={options} onReorder={onReorderOptions}>
        <Box flexDirection="column" gap="s16">
          {options.map((option, index) => {
            return (
              <Reorder.Item key={option.uuid} value={option}>
                <ChoiceElement
                  option={option}
                  index={index}
                  handleRemoveOption={handleRemoveOption}
                  handleUpdateOption={handleElementOptionChange}
                  handleUpdateOptionCorrect={handleElementOptionCorrectChange}
                  enableCorrectConfiguration={enableCorrectConfiguration}
                />
              </Reorder.Item>
            )
          })}
        </Box>
      </Reorder.Group>
      <Box flexDirection="row">
        <F0Button
          label={i18n.t('forms.editor.add_option_button')}
          icon={Add}
          onClick={addChoiceElement}
          variant="outline"
        />
      </Box>
    </>
  )
}

export default ChoiceElementEditor
